// app/javascript/controllers/dae20/offer_countdown_controller.js
import { Controller } from "@hotwired/stimulus"

/**
 * Countdown timer for drop offer cards.
 *
 * Shows remaining time before the offer expires and gets passed to another drop.
 * When timer reaches 0, the card fades out and is removed from DOM.
 *
 * Usage:
 * <div data-controller="dae20--offer-countdown"
 *      data-dae20--offer-countdown-expires-at-value="2025-01-01T12:01:00Z">
 *   <span data-dae20--offer-countdown-target="timer">1:00</span>
 *   <div data-dae20--offer-countdown-target="card">...card content...</div>
 * </div>
 */
export default class extends Controller {
  static values = {
    expiresAt: { type: String, default: "" }
  }

  static targets = ["timer", "card"]

  intervalId = null

  connect() {
    if (!this.expiresAtValue) {
      console.warn("[OfferCountdown] No expiresAt value")
      return
    }

    this.expiresTime = new Date(this.expiresAtValue).getTime()
    this.tick()
    this.intervalId = setInterval(() => this.tick(), 1000)
  }

  disconnect() {
    if (this.intervalId) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
  }

  tick() {
    const now = Date.now()
    const remaining = Math.max(0, Math.floor((this.expiresTime - now) / 1000))

    this.updateDisplay(remaining)
    this.updateColor(remaining)

    if (remaining <= 0) {
      this.expire()
    }
  }

  updateDisplay(seconds) {
    if (!this.hasTimerTarget) return

    const min = Math.floor(seconds / 60)
    const sec = seconds % 60
    this.timerTarget.textContent = `${min}:${sec.toString().padStart(2, "0")}`
  }

  updateColor(seconds) {
    if (!this.hasTimerTarget) return

    // Remove previous color classes
    this.timerTarget.classList.remove(
      "text-green-600", "text-yellow-600", "text-red-600",
      "animate-pulse"
    )

    if (seconds <= 10) {
      this.timerTarget.classList.add("text-red-600", "animate-pulse")
    } else if (seconds <= 30) {
      this.timerTarget.classList.add("text-yellow-600")
    } else {
      this.timerTarget.classList.add("text-green-600")
    }
  }

  expire() {
    if (this.intervalId) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }

    const card = this.hasCardTarget ? this.cardTarget : this.element
    card.style.transition = "opacity 0.5s ease-out, transform 0.5s ease-out"
    card.style.opacity = "0"
    card.style.transform = "scale(0.95)"

    setTimeout(() => {
      card.remove()
    }, 500)
  }
}
